import {
  FILTER_OPERATORS_FOR_BOOLEANS,
  FILTER_OPERATORS_FOR_DATES,
  FILTER_OPERATORS_FOR_ENUMS,
  FILTER_OPERATORS_FOR_NUMBERS,
  FILTER_OPERATORS_FOR_REFERENCES,
  FILTER_OPERATORS_FOR_STRINGS,
} from "./FilterChip/FilterChip.content";

import {
  FilterDefinitionType,
  SortableDefinitionsType,
  TableColumnType,
} from "@/components/@types";
import {
  SORTABLES_BOOLEAN_OPERATORS,
  SORTABLES_ENUM_OPERATORS,
  SORTABLES_NUMBER_OPERATORS,
  SORTABLES_STRING_OPERATORS,
} from "@/components/organisms/UtilityBar/SortChip/SortChip.content";

/**
 * Converts an array of columns into a filter definitions object.
 *
 * @param columns - An array of columns, each containing a key, label, and fieldType.
 * @returns An object where each key corresponds to a column key and the value is a filter definition.
 *
 * The filter definition includes:
 * - `type`: The type of the filter (string, number, or date).
 * - `name`: The label of the column.
 * - `operatorGroupings`: A set of operators that are mutually exclusive for the filter type.
 */
export function columnsToFilterDefinitions(columns: TableColumnType[]): {
  [key: string]: FilterDefinitionType;
} {
  const filterDefinitions = {} as Record<string, FilterDefinitionType>;

  for (const column of columns) {
    if (column.isFilterable) {
      const key = column.inputKey || column.objectKey;

      if (["string", "text"].includes(column.type)) {
        filterDefinitions[key] = {
          type: "text",
          name: column.label,
          operators: FILTER_OPERATORS_FOR_STRINGS,
          ReactIcon: column.ReactIcon,
        };
      } else if (column.type === "number") {
        filterDefinitions[key] = {
          type: "number",
          name: column.label,
          operators: FILTER_OPERATORS_FOR_NUMBERS,
          ReactIcon: column.ReactIcon,
        };
      } else if (column.type === "currency") {
        filterDefinitions[key] = {
          type: "currency",
          name: column.label,
          operators: FILTER_OPERATORS_FOR_NUMBERS,
          ReactIcon: column.ReactIcon,
        };
      } else if (column.type === "date") {
        filterDefinitions[key] = {
          type: "date",
          name: column.label,
          operators: FILTER_OPERATORS_FOR_DATES,
          ReactIcon: column.ReactIcon,
        };
      } else if (column.type === "reference" || column.type === "references") {
        // A column of several references (a transaction's tags) filters the
        // same way as one: by the records it points at.
        //
        // Probe ``getValue`` with an empty input to recover the
        // ``__typename`` the column references. Columns are written so
        // ``getValue(undefined)`` returns the shape with ``__typename``
        // populated and the data fields blank — that's the cheapest way
        // to introspect the target type without threading a separate
        // schema through every column.
        let referenceTypename: string | undefined;
        try {
          const getValue = (column as { getValue?: (d: unknown) => unknown })
            .getValue;
          // A column of several references maps each record it holds, so it
          // is asked with one blank record to name the type.
          const probed =
            column.type === "references"
              ? getValue?.({ [column.objectKey]: [{}] })
              : getValue?.(undefined);
          const probe = Array.isArray(probed) ? probed[0] : probed;
          if (probe && typeof probe === "object" && "__typename" in probe) {
            referenceTypename = String(
              (probe as { __typename?: unknown }).__typename ?? "",
            );
          }
        } catch {
          // Some columns deref ``data`` unsafely on undefined input; if
          // so we just leave ``referenceTypename`` undefined and the
          // workflow filter picker falls back to "any reference object".
        }
        filterDefinitions[key] = {
          type: "reference",
          name: column.label,
          operators: FILTER_OPERATORS_FOR_REFERENCES,
          getValueOptions: column?.getValueOptions,
          ReactIcon: column.ReactIcon,
          referenceTypename: referenceTypename || undefined,
        };
      } else if (column.type === "enum") {
        filterDefinitions[key] = {
          type: "enum",
          name: column.label,
          operators: FILTER_OPERATORS_FOR_ENUMS,
          getValueOptions: column?.getValueOptions,
          ReactIcon: column.ReactIcon,
        };
      } else if (column.type === "enums") {
        filterDefinitions[key] = {
          type: "enums",
          name: column.label,
          operators: FILTER_OPERATORS_FOR_ENUMS,
          getValueOptions: column?.getValueOptions,
          ReactIcon: column.ReactIcon,
        };
      } else if (column.type === "boolean") {
        filterDefinitions[key] = {
          type: "boolean",
          name: column.label,
          operators: FILTER_OPERATORS_FOR_BOOLEANS,
          ReactIcon: column.ReactIcon,
        };
      } else {
        // throw new Error(`Unsupported filter type: ${column.type}`);
      }
    }
  }
  return filterDefinitions;
}

/**
 * Converts an array of column definitions into a mapping of sort definitions.
 *
 * @param columns - An array of column definitions, where each column has a type, key, and label.
 * @returns An object where each key is a column key and the value is an object containing
 *          the type, name, and operator groupings for sorting.
 *
 * @example
 * const columns = [
 *   { type: "string", key: "name", label: "Name" },
 *   { type: "number", key: "age", label: "Age" },
 *   { type: "date", key: "birthdate", label: "Birthdate" }
 * ];
 * const sortDefinitions = columnsToSortDefinitions(columns);
 * // sortDefinitions will be:
 * // {
 * //   name: { type: "text", name: "Name", operatorGroupings: ORDERING_STRING_GROUPINGS },
 * //   age: { type: "number", name: "Age", operatorGroupings: ORDERING_STRING_GROUPINGS },
 * //   birthdate: { type: "date", name: "Birthdate", operatorGroupings: ORDERING_STRING_GROUPINGS }
 * // }
 */
export function columnsToSortableDefinitions(columns: TableColumnType[]): {
  [key: string]: SortableDefinitionsType;
} {
  const sortDefinitions = {} as Record<string, SortableDefinitionsType>;

  for (const column of columns) {
    const key = column.inputKey || column.objectKey;

    if (column.isSortable) {
      if (["string", "text"].includes(column.type)) {
        sortDefinitions[key] = {
          type: "text",
          name: column.label,
          operators: SORTABLES_STRING_OPERATORS,
          ReactIcon: column.ReactIcon,
        };
      } else if (column.type === "currency") {
        sortDefinitions[key] = {
          type: "currency",
          name: column.label,
          operators: SORTABLES_NUMBER_OPERATORS,
          ReactIcon: column.ReactIcon,
        };
      } else if (column.type === "number") {
        sortDefinitions[key] = {
          type: "number",
          name: column.label,
          operators: SORTABLES_NUMBER_OPERATORS,
          ReactIcon: column.ReactIcon,
        };
      } else if (column.type === "enum") {
        sortDefinitions[key] = {
          type: "enum",
          name: column.label,
          operators: SORTABLES_ENUM_OPERATORS,
          ReactIcon: column.ReactIcon,
        };
      } else if (column.type === "enums") {
        sortDefinitions[key] = {
          type: "enums",
          name: column.label,
          operators: SORTABLES_ENUM_OPERATORS,
          ReactIcon: column.ReactIcon,
        };
      } else if (column.type === "date") {
        sortDefinitions[key] = {
          type: "date",
          name: column.label,
          operators: SORTABLES_STRING_OPERATORS,
          ReactIcon: column.ReactIcon,
        };
      } else if (column.type === "boolean") {
        sortDefinitions[key] = {
          type: "boolean",
          name: column.label,
          operators: SORTABLES_BOOLEAN_OPERATORS,
          ReactIcon: column.ReactIcon,
        };
      } else if (column.type === "reference") {
        sortDefinitions[key] = {
          type: "text",
          name: column.label,
          operators: SORTABLES_STRING_OPERATORS,
          ReactIcon: column.ReactIcon,
        };
      } else {
        // throw new Error(`Unsupported sortable type: ${column.type}`);
      }
    }
  }
  return sortDefinitions;
}
